import React from 'react';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../../theme';
import { baseButton, nextIndex } from './shared';
import { Menu } from './Menu';

export type TabItem<K extends string> = { key: K; label: React.ReactNode; count?: number; disabled?: boolean };

/**
 * Understrukna flikar, 48 px höga, scrollar horisontellt på smal skärm.
 * Med `maxVisible` hamnar resten i en "Mer"-meny; är den aktiva fliken där visas den tillfälligt i raden.
 * Med `fit` (UX13) mäts radens bredd: så många flikar som får plats visas och bara resten hamnar under "Mer"
 * (inget "Mer" när allt får plats). `maxVisible` gäller då tills första mätningen är klar.
 * Piltangenter byter flik (automatisk aktivering), Home/End går till första/sista.
 */
export function Tabs<K extends string>({ items, value, onChange, label, maxVisible, fit = false, moreLabel = 'Mer', idPrefix = 'tab', style }: {
  items: TabItem<K>[]; value: K; onChange: (k: K) => void;
  /** Flikradens namn för skärmläsare. */
  label: string;
  maxVisible?: number; moreLabel?: string;
  /** Visa så många flikar som får plats i bredd (ResizeObserver); resten under "Mer". */
  fit?: boolean;
  /** Prefix för id så att panelen kan peka tillbaka med aria-labelledby={`${idPrefix}-${key}`}. */
  idPrefix?: string;
  style?: React.CSSProperties;
}) {
  const T = useTheme();
  const refs = React.useRef(new Map<K, HTMLButtonElement | null>());
  const rowRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const [fitted, setFitted] = React.useState<number | null>(null);
  const measureKey = items.map((t) => `${t.key}:${t.count ?? ''}`).join('|');
  React.useLayoutEffect(() => {
    if (!fit) { setFitted(null); return; }
    const row = rowRef.current, meas = measureRef.current;
    if (!row || !meas) return;
    const measure = () => {
      const kids = Array.from(meas.children) as HTMLElement[];
      const widths = kids.slice(0, items.length).map((el) => el.getBoundingClientRect().width);
      const moreW = kids[items.length]?.getBoundingClientRect().width ?? 0;
      setFitted(fitTabCount(widths, row.clientWidth, TAB_GAP, moreW, items.findIndex((t) => t.key === value)));
    };
    measure();
    // Fönstrets resize som reserv (ResizeObserver saknas i äldre WebKit).
    window.addEventListener('resize', measure);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(row);
    return () => { window.removeEventListener('resize', measure); ro?.disconnect(); };
  }, [fit, measureKey, value]); // eslint-disable-line react-hooks/exhaustive-deps
  const limit = fit && fitted !== null ? fitted : maxVisible;
  const head = limit !== undefined && items.length > limit ? items.slice(0, limit) : items;
  const rest = items.slice(head.length);
  const activeInRest = rest.find((t) => t.key === value);
  const shown = activeInRest ? [...head, activeInRest] : head;

  const onKey = (e: React.KeyboardEvent) => {
    const cur = shown.findIndex((t) => t.key === value);
    const i = nextIndex(e.key, Math.max(0, cur), shown.map((t) => !!t.disabled));
    if (i === null) return;
    e.preventDefault();
    const k = shown[i].key;
    onChange(k);
    refs.current.get(k)?.focus();
  };

  const tabStyle = (on: boolean, disabled?: boolean): React.CSSProperties => ({
    ...baseButton, position: 'relative', height: 48, padding: '0 2px', background: 'none', borderRadius: 0,
    fontSize: 15, fontWeight: 700, color: on ? T.text : T.textMuted, opacity: disabled ? 0.5 : 1,
    boxShadow: on ? `inset 0 -2px 0 ${T.amber}` : 'none', flexShrink: 0,
  });
  const countBadge = (t: TabItem<K>) => !!t.count && <span style={{ fontSize: 12, fontWeight: 700, color: T.onAccent, background: T.accentFill, borderRadius: 999, padding: '2px 7px' }}>{t.count}</span>;
  const moreStyle: React.CSSProperties = { ...baseButton, height: 48, padding: '0 2px', background: 'none', fontSize: 15, fontWeight: 700, color: T.textMuted, gap: 4 };

  return (
    <div ref={rowRef} style={{ display: 'flex', alignItems: 'stretch', borderBottom: `1px solid ${T.border}`, position: fit ? 'relative' : undefined, ...style }}>
      {/* UX13: osynlig kopia av alla flikar (och Mer) för att mäta bredderna. */}
      {fit && (
        <div ref={measureRef} aria-hidden style={{ position: 'absolute', left: 0, top: 0, height: 0, overflow: 'hidden', visibility: 'hidden', display: 'flex', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
          {items.map((t) => <span key={t.key} style={tabStyle(true)}>{t.label}{countBadge(t)}</span>)}
          <span style={moreStyle}>{moreLabel}<ChevronDown size={18} strokeWidth={2} aria-hidden /></span>
        </div>
      )}
      <div role="tablist" aria-label={label} onKeyDown={onKey} style={{
        display: 'flex', gap: TAB_GAP, overflowX: 'auto', scrollbarWidth: 'none', flex: '0 1 auto', minWidth: 0,
      }}>
        {shown.map((t) => {
          const on = t.key === value;
          return (
            <button key={t.key} ref={(el) => { refs.current.set(t.key, el); }} id={`${idPrefix}-${t.key}`}
              type="button" role="tab" aria-selected={on} tabIndex={on ? 0 : -1} disabled={t.disabled}
              onClick={() => onChange(t.key)}
              style={tabStyle(on, t.disabled)}
            >
              {t.label}
              {countBadge(t)}
            </button>
          );
        })}
      </div>
      {rest.some((t) => t !== activeInRest) && (
        <div style={{ marginLeft: TAB_GAP, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Menu align="right"
            items={rest.filter((t) => t !== activeInRest).map((t) => ({ key: t.key, label: t.label, disabled: t.disabled, onSelect: () => onChange(t.key) }))}
            trigger={(p) => (
              <button type="button" {...p} style={moreStyle}>
                {moreLabel}<ChevronDown size={18} strokeWidth={2} aria-hidden />
              </button>
            )}
          />
        </div>
      )}
    </div>
  );
}

const TAB_GAP = 24;

/**
 * UX13: hur många flikar (i ordning) som får plats före "Mer". Alla om allt får plats (inget Mer). Annars plats
 * för Mer-knappen och, om den aktiva fliken ligger bland resten, för den också (den visas tillfälligt i raden).
 */
export function fitTabCount(widths: number[], avail: number, gap: number, moreW: number, activeIndex: number): number {
  const n = widths.length;
  const sum = (k: number) => widths.slice(0, k).reduce((a, b) => a + b, 0);
  if (sum(n) + gap * Math.max(0, n - 1) <= avail) return n;
  for (let k = n - 1; k > 0; k--) {
    const extra = activeIndex >= k ? widths[activeIndex] : 0;
    const count = k + (activeIndex >= k ? 1 : 0);
    if (sum(k) + extra + gap * (count - 1) + gap + moreW <= avail) return k;
  }
  return 0;
}
